import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ShieldCheck, Clock, Tag, CheckCircle2, Loader2, X, PartyPopper } from 'lucide-react';
import useSeo from '../hooks/useSeo';
import { SiteHeader, SiteFooter } from './ServicesIndex';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DatePickerPro } from '../components/ui/date-picker-pro';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

const TIME_SLOTS = ['08:00 – 10:00', '10:00 – 12:00', '12:00 – 14:00', '14:00 – 16:00', '16:00 – 18:00', '18:00 – 20:00'];

export default function SpecialOffers({ onOpenCalculator }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null); // offer being booked
  const [campaign, setCampaign] = useState(null);
  const [searchParams] = useSearchParams();
  const heroCampaign = searchParams.get('campaign') === 'hero';

  useSeo(
    'Special Offers & Festive Deals',
    'Grab limited-time festive and seasonal pest control offers from EntoRid — flat discounts, extended warranties and same-day service on termite, cockroach, rodent and bed bug treatments.'
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, hero] = await Promise.all([
        axios.get(`${API}/public/special-offers`),
        axios.get(`${API}/public/hero-special-offer`),
      ]);
      setOffers(res.data.offers || []);
      setCampaign(hero.data || null);
    } catch (e) {
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const featuredOffers = offers.filter((offer) => offer.hero_featured);
  // A new campaign can be switched on before its three sector offers are marked.
  // Keep the customer journey useful by falling back to live special offers.
  const displayedOffers = heroCampaign && featuredOffers.length ? featuredOffers : offers;

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Poppins, Inter, sans-serif' }} data-testid="special-offers-page">
      <SiteHeader onOpenCalculator={onOpenCalculator} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs sm:text-sm text-gray-500">
          <Link to="/" className="hover:text-green-700">Home</Link>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">Special Offers</span>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-rose-900 via-slate-900 to-amber-800 text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #f59e0b 0, transparent 40%), radial-gradient(circle at 80% 70%, #e11d48 0, transparent 40%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-400/20 border border-amber-300/40 px-4 py-1.5 text-xs font-semibold text-amber-200 mb-5">
            <PartyPopper size={14} /> LIMITED-TIME SEASONAL DEALS
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl">
            {heroCampaign ? (campaign?.campaign_name || 'Featured Seasonal Offers') : "Festive Offers on India's most trusted pest control"}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/80 max-w-2xl">
            {heroCampaign ? (campaign?.campaign_subtitle || 'Choose the offer designed for your home, business or industrial site.') : 'Hand-picked seasonal deals with real savings, extended warranties and priority scheduling. Book in under a minute — no payment needed to reserve.'}
          </p>
        </div>
      </section>

      {/* OFFERS GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400" data-testid="offers-loading">
            <Loader2 className="animate-spin mr-2" /> Loading offers…
          </div>
        ) : displayedOffers.length === 0 ? (
          <div className="text-center py-24" data-testid="offers-empty">
            <Tag className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-lg font-semibold text-slate-700">No active offers right now</h3>
            <p className="text-slate-500 mt-1">The campaign will go live when the admin enables a matching seasonal offer.</p>
            <Link to="/"><Button className="mt-6 bg-emerald-700 hover:bg-emerald-800" data-testid="offers-empty-home">Explore our services</Button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="offers-grid">
            {displayedOffers.map((o) => <OfferCard key={o.id} offer={o} onBook={() => setBooking(o)} />)}
          </div>
        )}
      </section>

      <SiteFooter />

      {booking && (
        <BookOfferModal
          offer={booking}
          onClose={() => setBooking(null)}
          onBooked={() => { setBooking(null); load(); }}
        />
      )}
    </div>
  );
}

function OfferCard({ offer, onBook }) {
  const pct = offer.discount_type === 'percent' ? `${offer.discount_value}% OFF` : offer.discount_amount > 0 ? `${inr(offer.discount_amount)} OFF` : 'SPECIAL PRICE';
  return (
    <div className="group relative flex flex-col rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`offer-card-${offer.slug}`}>
      <div className="relative h-40 bg-gradient-to-br from-rose-600 to-amber-500 flex items-center justify-center overflow-hidden">
        {offer.banner_image_url
          ? <img src={offer.banner_image_url} alt={offer.title} className="w-full h-full object-cover" />
          : <Sparkles className="text-white/90" size={56} />}
        <span className="absolute top-3 left-3 rounded-full bg-black/40 backdrop-blur px-3 py-1 text-[11px] font-bold tracking-wide text-white">{offer.badge_text || 'SPECIAL OFFER'}</span>
        <span className="absolute top-3 right-3 rounded-full bg-amber-400 px-3 py-1 text-[11px] font-extrabold text-slate-900">{pct}</span>
      </div>
      <div className="flex flex-col flex-1 p-5">
        <h3 className="text-lg font-bold text-slate-900">{offer.title}</h3>
        {offer.description && <p className="mt-1.5 text-sm text-slate-500 line-clamp-2">{offer.description}</p>}

        <div className="mt-4 flex items-end gap-2">
          <span className="text-2xl font-extrabold text-emerald-700">{inr(offer.offer_price)}</span>
          {offer.original_price > offer.offer_price && (
            <span className="text-sm text-slate-400 line-through mb-0.5">{inr(offer.original_price)}</span>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
          {offer.warranty_months > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 font-semibold"><ShieldCheck size={12} /> {offer.warranty_months}-month warranty</span>
          )}
          {offer.validity_end && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2.5 py-1 font-semibold"><Clock size={12} /> Till {offer.validity_end.slice(0, 10)}</span>
          )}
        </div>

        {offer.highlights?.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {offer.highlights.slice(0, 4).map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" /> {h}</li>
            ))}
          </ul>
        )}
          {offer.hero_featured && <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-700 px-2.5 py-1 font-semibold">{offer.campaign_sector} campaign</span>}

        <Button onClick={onBook} className="mt-5 w-full bg-rose-700 hover:bg-rose-800 text-white font-semibold" data-testid={`offer-book-btn-${offer.slug}`}>
          Grab this offer
        </Button>
      </div>
    </div>
  );
}

function BookOfferModal({ offer, onClose, onBooked }) {
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    customer_city: '', customer_address: '', scheduled_date: '', time_slot: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // booking_code

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.customer_name.trim() || form.customer_name.trim().length < 2) return toast.error('Please enter your name');
    if (!/^\d{10}$/.test((form.customer_phone || '').replace(/\D/g, '').slice(-10))) return toast.error('Enter a valid 10-digit phone number');
    if (!form.scheduled_date) return toast.error('Please choose a preferred date');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/special-offers/${offer.slug}/book`, {
        ...form,
        customer_phone: (form.customer_phone || '').replace(/\D/g, '').slice(-10),
        customer_email: form.customer_email || undefined,
      });
      setDone(res.data.booking_code);
      toast.success('Offer booked! Our team will call you shortly.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not book this offer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="offer-book-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between bg-gradient-to-r from-rose-700 to-amber-600 text-white px-5 py-4 rounded-t-2xl">
          <div>
            <div className="text-xs font-semibold text-white/80">{offer.badge_text || 'SPECIAL OFFER'}</div>
            <div className="text-lg font-bold leading-tight">{offer.title}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/15" data-testid="offer-modal-close"><X size={20} /></button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="offer-book-success">
            <CheckCircle2 className="mx-auto text-emerald-600 mb-4" size={56} />
            <h3 className="text-xl font-bold text-slate-900">Booking confirmed!</h3>
            <p className="text-slate-500 mt-1">Your offer booking reference is</p>
            <div className="mt-3 inline-block rounded-lg bg-slate-900 text-amber-300 font-mono text-lg px-4 py-2 tracking-wider" data-testid="offer-booking-code">{done}</div>
            <p className="text-sm text-slate-500 mt-4">Our team will call you within 2 working hours to confirm your slot.</p>
            <Button onClick={onBooked} className="mt-6 bg-emerald-700 hover:bg-emerald-800" data-testid="offer-success-done">Done</Button>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3">
              <span className="text-sm text-slate-600">Offer price</span>
              <span className="text-xl font-extrabold text-emerald-700">{inr(offer.offer_price)}
                {offer.original_price > offer.offer_price && <span className="ml-2 text-sm text-slate-400 line-through font-normal">{inr(offer.original_price)}</span>}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Full Name *</Label><Input value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} data-testid="offer-form-name" /></div>
              <div><Label>Mobile *</Label><Input value={form.customer_phone} onChange={(e) => set('customer_phone', e.target.value)} placeholder="10-digit number" data-testid="offer-form-phone" /></div>
              <div><Label>Email</Label><Input type="email" value={form.customer_email} onChange={(e) => set('customer_email', e.target.value)} data-testid="offer-form-email" /></div>
              <div><Label>City</Label><Input value={form.customer_city} onChange={(e) => set('customer_city', e.target.value)} data-testid="offer-form-city" /></div>
              <div><Label>Preferred Date *</Label><DatePickerPro value={form.scheduled_date} onChange={(v) => set('scheduled_date', v)} data-testid="offer-form-date" /></div>
              <div className="sm:col-span-2">
                <Label>Preferred Time Slot</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {TIME_SLOTS.map((s) => (
                    <button key={s} type="button" onClick={() => set('time_slot', s)} data-testid={`offer-slot-${s.slice(0,5)}`}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border ${form.time_slot === s ? 'bg-rose-700 text-white border-rose-700' : 'bg-white text-slate-600 border-slate-300'}`}>{s}</button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2"><Label>Address</Label><Input value={form.customer_address} onChange={(e) => set('customer_address', e.target.value)} data-testid="offer-form-address" /></div>
              <div className="sm:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Anything we should know?" data-testid="offer-form-notes" /></div>
            </div>
            <Button onClick={submit} disabled={submitting} className="w-full bg-rose-700 hover:bg-rose-800 font-semibold mt-2" data-testid="offer-form-submit">
              {submitting ? <><Loader2 size={16} className="animate-spin mr-2" /> Booking…</> : 'Confirm Booking'}
            </Button>
            {offer.terms && <p className="text-[11px] text-slate-400 text-center">{offer.terms}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
